// src/components/Sidebar.tsx
import React, { useEffect, useState } from "react";
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, BookOpen, Puzzle, Users, BarChart, Lock, ClipboardCheck, TrendingUp, Menu, X, User } from 'lucide-react';
import ScoreCard from './ScoreCard';
import { useMsal } from "@azure/msal-react";

const Sidebar = () => {
  const location = useLocation(); // ✅ proper hook usage
  const { accounts } = useMsal();
  const user = accounts[0];
  const [hasRoleAdmin, setHasRoleAdmin] = useState(false);
  const [hasRoleAssessment, setHasRoleAssessment] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
      if (!user) {
        setHasRoleAdmin(false);
        setHasRoleAssessment(false);
        return;
      }

      // Check if user has ChatGPT.Admin role (assuming roles in idToken claims)
      const roles = (user.idTokenClaims?.roles || []) as string[];

      if (roles.includes("ChatGPT.Admin")) {
        setHasRoleAdmin(true);
      }
      else {
        setHasRoleAdmin(false);
      }

      if (roles.includes("ChatGPT.Assessment")) {
        setHasRoleAssessment(true);
      }
      else {
        setHasRoleAssessment(false);
      }
  }, [user]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-gradient-to-b from-[#0f111a] to-[#0a0c18] border border-white/10 text-white hover:bg-white/5 transition print:hidden"
        aria-label="Toggle navigation menu"
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 print:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        h-full w-64 text-white flex flex-col p-4 space-y-4
        bg-gradient-to-b from-[#0f111a] to-[#0a0c18] border-r border-white/5 shadow-lg
        print:hidden
        lg:relative lg:translate-x-0
        fixed top-0 left-0 z-40 transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="text-2xl font-bold text-white mb-6 flex items-center gap-1 mt-12 lg:mt-0">
          <img src="/AICoach.png" alt="AI Coach Logo" />
        </div>

      <nav className="flex flex-col gap-2 text-sm font-medium">
        <Link
          to="/"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname === '/' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <LayoutDashboard className="w-5 h-5" />
          <span>Introduction</span>
        </Link>

        <Link
          to="/lessons"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname === '/lessons' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <BookOpen className="w-5 h-5" />
          <span>Lessons</span>
        </Link>

        <Link
          to="/challenges"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname.startsWith('/challenges') ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <Puzzle className="w-5 h-5" />
          <span>Challenges</span>
        </Link>
        {hasRoleAssessment && (
        <Link
          to="/role-selection"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname === '/role-selection' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <User className="w-5 h-5" />
          <span>Role Selection</span>
        </Link>
        )}

        {hasRoleAssessment && (
        <Link
          to="/assessments"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname.startsWith('/assessments') ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <ClipboardCheck className="w-5 h-5" />
          <span>Assessments</span>
        </Link>
        )}

        {hasRoleAssessment && (
        <Link
          to="/skills"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname === '/skills' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <TrendingUp className="w-5 h-5" />
          <span>Assessment Results</span>
        </Link>
        )}

        <Link
          to="/team"
          className={`flex items-center gap-3 px-3 py-2 rounded-md transition ${
            location.pathname === '/team' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
          }`}
        >
          <Users className="w-5 h-5" />
          <span>Team</span>
        </Link>

        {hasRoleAdmin && (
          <Link
            to="/analytics"
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
              location.pathname === '/admin' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
            }`}
          >
            <BarChart className="w-5 h-5" />
            <span className="flex items-center gap-1">
              Admin Analytics
              <Lock className="w-3 h-3 text-gray-400" />
            </span>
          </Link>
        )}

        {hasRoleAssessment && hasRoleAdmin && (
          <Link
            to="/admin/assessment-results"
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
              location.pathname === '/admin/assessment-results' ? 'bg-white/10 text-primary' : 'hover:bg-white/5'
            }`}
          >
            <ClipboardCheck className="w-5 h-5" />
            <span className="flex items-center gap-1">
              Admin Assessments
              <Lock className="w-3 h-3 text-gray-400" />
            </span>
          </Link>
        )}
      </nav>

        <div className="text-2xl font-bold text-white pt-10 flex items-center justify-center gap-1">
          <ScoreCard />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
